import { ClipUtils } from '@air/utils-clips';

import { type PrivateImporterJobItem } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateImporterJobItemsList';
import { type PrivateWorkspaceAgentClipsListByIdsRows } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { type FinishedImportTask } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/getLatestFinishedImportTask';
import {
  type FinishedUploadBatch,
  type UploadedBatchAttachment,
} from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';

export type ImportBatchPreviewClip = Pick<
  PrivateWorkspaceAgentClipsListByIdsRows[number],
  'assets' | 'id' | 'importedName' | 'status' | 'title'
>;

export type BuildFinishedImportBatchParams = {
  items: readonly PrivateImporterJobItem[];
  previewClips: readonly ImportBatchPreviewClip[];
  task: FinishedImportTask;
};

export const buildFinishedImportBatch = ({
  items,
  previewClips,
  task,
}: BuildFinishedImportBatchParams): FinishedUploadBatch | null => {
  const previewClipsById = new Map(previewClips.map((clip) => [clip.id, clip]));
  const attachments = items.flatMap((item): UploadedBatchAttachment[] => {
    if (item.type !== 'assetVersion') {
      return [];
    }

    const clip = previewClipsById.get(item.assetVersionId);
    // The still's URL can be set before the CDN serves it, so a processing clip has no still yet.
    const isThumbnailPending = !!clip && ClipUtils.isProcessing(clip.status);

    return [
      {
        assetId: item.assetId,
        id: item.assetVersionId,
        isThumbnailPending,
        name: clip?.title || clip?.importedName || 'Untitled',
        thumbnailUrl: isThumbnailPending ? null : clip?.assets.image || null,
        versionId: item.assetVersionId,
      },
    ];
  });

  if (attachments.length === 0) {
    return null;
  }

  return {
    attachments,
    batchId: task.taskId,
    finishedAt: task.finishedAt,
    source: 'import',
  };
};
